import { createMiddleware, createStart } from '@tanstack/react-start';
import { APP_VERSION_HEADER, APP_VERSION_STORAGE_KEY } from '@/lib/app-version';
import { telemetryHeaders } from '@/lib/telemetry-headers';

function syncVersionFromHeader(response: Response) {
	if (typeof window === 'undefined') {
		return;
	}

	const version = response.headers.get(APP_VERSION_HEADER);
	if (!version) {
		return;
	}

	const currentVersion = window.localStorage.getItem(APP_VERSION_STORAGE_KEY);
	if (!currentVersion) {
		window.localStorage.setItem(APP_VERSION_STORAGE_KEY, version);
		return;
	}

	if (currentVersion !== version) {
		window.localStorage.setItem(APP_VERSION_STORAGE_KEY, version);
	}
}

/**
 * Attach the browser-side telemetry headers (timezone, viewport, running app version, trace/session
 * ids) to a server-function call. Only meaningful in the browser — `telemetryHeaders()` returns an
 * empty object on the server, where this same fetch is used for in-process calls.
 *
 * Merged rather than replaced: TanStack Start sets its own headers on server-function requests
 * (`x-tsr-serverFn`, content type), and clobbering them would break the call.
 *
 * Guarded, because this sits on the path of EVERY server-function call: `Headers.set` throws on a value
 * containing characters a header can't hold, and `x-app-version` comes out of localStorage. A telemetry
 * header we can't attach must cost us an attribute, never the call it was riding on.
 */
function withTelemetryHeaders(init: RequestInit | undefined): RequestInit | undefined {
	try {
		const extra = telemetryHeaders();
		if (Object.keys(extra).length === 0) return init;
		const headers = new Headers(init?.headers);
		for (const [name, value] of Object.entries(extra)) headers.set(name, value);
		return { ...init, headers };
	} catch {
		return init;
	}
}

const appVersionAwareFetch = Object.assign((...args: Parameters<typeof fetch>) => {
	const [input, init] = args;
	return fetch(input, withTelemetryHeaders(init)).then((response) => {
		syncVersionFromHeader(response);
		return response;
	});
}, fetch);

const appVersionHeaderMiddleware = createMiddleware({ type: 'request' }).server(async (options) => {
	const result = await options.next();

	if (result?.response?.headers) {
		const deployedVersion = process.env.VITE_APP_VERSION ?? 'dev-local';
		result.response.headers.set(APP_VERSION_HEADER, deployedVersion);
	}
	return result;
});

// Canonical request wide event: one event per HTTP request, enriched mid-request by
// handlers (see `@/lib/wide-event.server`) and emitted once at the end. The shared core
// buffers + batch-sends, so this never blocks the response.
//
// `wide-event.server` is server-only (pulls in sst + the monolith transport). It's loaded
// via dynamic import *inside* `.server()` so it never enters the client bundle (this start
// instance is isomorphic — `serverFns.fetch` runs in the browser).
const requestWideEventMiddleware = createMiddleware({ type: 'request' }).server(async (options) => {
	const wide = await import('@/lib/wide-event.server');
	// `options.next()` may return a value or a promise; normalize to a promise.
	return wide.instrumentPortalsRequest(() => Promise.resolve(options.next()));
});

export const startInstance = createStart(() => ({
	// `requestWideEventMiddleware` stays FIRST: the array runs outside-in, so only the first entry wraps
	// every other middleware AND the route handler, which is what lets one event carry the status the
	// caller actually received. Anything placed before it would hide later status rewrites from the
	// event — and a status the reporter mis-reads is a system-error ticket either raised for a client's
	// fault or lost for ours.
	requestMiddleware: [requestWideEventMiddleware, appVersionHeaderMiddleware],
	serverFns: {
		fetch: appVersionAwareFetch,
	},
}));
